import { useState, useCallback } from 'react'
import { FranchisePartner } from './useFranchisePartnersData'

interface UseBulkOperationsReturn {
  selectedFranchises: Set<string>
  handleSelectFranchise: (fpCode: string) => void
  handleSelectAll: (franchisePartners: FranchisePartner[]) => void
  handleSelectAllFiltered: (franchisePartners: FranchisePartner[]) => void
  clearSelection: () => void
  isSelected: (fpCode: string) => boolean
  isAllSelected: (franchisePartners: FranchisePartner[]) => boolean
  isAllFilteredSelected: (filteredData: FranchisePartner[]) => boolean
  isIndeterminate: (franchisePartners: FranchisePartner[]) => boolean
}

export const useBulkOperations = (): UseBulkOperationsReturn => {
  const [selectedFranchises, setSelectedFranchises] = useState<Set<string>>(new Set())

  const handleSelectFranchise = useCallback((fpCode: string) => {
    setSelectedFranchises(prev => {
      const newSelected = new Set(prev)
      if (newSelected.has(fpCode)) {
        newSelected.delete(fpCode)
      } else {
        newSelected.add(fpCode)
      }
      return newSelected
    })
  }, [])

  const handleSelectAll = useCallback((franchisePartners: FranchisePartner[]) => {
    const allSelected = franchisePartners.every(fp => selectedFranchises.has(fp.fp_code))

    if (allSelected) {
      // Deselect all
      setSelectedFranchises(new Set())
    } else {
      // Select all visible franchises
      const allFpCodes = new Set(franchisePartners.map(fp => fp.fp_code))
      setSelectedFranchises(allFpCodes)
    }
  }, [selectedFranchises])

  const handleSelectAllFiltered = useCallback((franchisePartners: FranchisePartner[]) => {
    const allFpCodes = new Set(franchisePartners.map(fp => fp.fp_code))
    setSelectedFranchises(allFpCodes)
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedFranchises(new Set())
  }, [])

  const isSelected = useCallback((fpCode: string) => {
    return selectedFranchises.has(fpCode)
  }, [selectedFranchises])

  const isAllSelected = useCallback((franchisePartners: FranchisePartner[]) => {
    return franchisePartners.length > 0 &&
           franchisePartners.every(fp => selectedFranchises.has(fp.fp_code))
  }, [selectedFranchises])

  const isAllFilteredSelected = useCallback((filteredData: FranchisePartner[]) => {
    return filteredData.length > 0 &&
           filteredData.every(fp => selectedFranchises.has(fp.fp_code))
  }, [selectedFranchises])

  const isIndeterminate = useCallback((franchisePartners: FranchisePartner[]) => {
    return franchisePartners.some(fp => selectedFranchises.has(fp.fp_code)) &&
           !franchisePartners.every(fp => selectedFranchises.has(fp.fp_code))
  }, [selectedFranchises])

  return {
    selectedFranchises,
    handleSelectFranchise,
    handleSelectAll,
    handleSelectAllFiltered,
    clearSelection,
    isSelected,
    isAllSelected,
    isAllFilteredSelected,
    isIndeterminate
  }
}